import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getGoldLists,
  getGoldList,
  createGoldList,
  updateGoldList,
  deleteGoldList,
  previewGoldBatch,
  executeGoldBatch,
} from "../api";

// Generate a UUID for idempotency (double-submit protection)
const genKey = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {
    /* ignore */
  }
  return `gold-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const money = (v) => {
  const n = num(v);
  return `${n < 0 ? "-" : ""}$${Math.abs(n).toFixed(2)}`;
};

/**
 * Manual Gold (XAUUSD) batch entry.
 * Props:
 *   users: [{ user_id, username, balance, ... }]
 *   styles: theme style object from AdminDashboard (styles.card, .secondaryCard, .input, .muted, ...)
 *   onAfterExecute: () => void  (refresh parent users after a batch runs)
 *   setStatus: ({type, message}) => void  (optional shared status banner)
 */
export default function ManualGoldEntry({ users = [], styles = {}, onAfterExecute, setStatus }) {
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [currentList, setCurrentList] = useState(null); // { id, name, members: [...] }

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftMembers, setDraftMembers] = useState([]); // [{ user_id, qty, slippage, swap }]
  const [addUserId, setAddUserId] = useState("");
  const [saving, setSaving] = useState(false);

  // Execute panel state
  const [side, setSide] = useState("buy");
  const [entryPrice, setEntryPrice] = useState("");
  const [exitPrice, setExitPrice] = useState("");
  const [tradeDate, setTradeDate] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [localMsg, setLocalMsg] = useState(null);

  const notify = useCallback(
    (payload) => {
      if (setStatus) setStatus(payload);
      setLocalMsg(payload);
    },
    [setStatus]
  );

  const usersById = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => {
      m[u.user_id] = u;
    });
    return m;
  }, [users]);

  const loadLists = useCallback(async () => {
    try {
      const data = await getGoldLists();
      setLists(Array.isArray(data) ? data : []);
    } catch (err) {
      notify({ type: "error", message: "Failed to load gold lists." });
    }
  }, [notify]);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const loadListDetail = useCallback(
    async (listId) => {
      if (!listId) {
        setCurrentList(null);
        return;
      }
      try {
        const data = await getGoldList(listId);
        setCurrentList(data);
        setPreview(null);
        setResult(null);
      } catch (err) {
        notify({ type: "error", message: "Failed to load list." });
      }
    },
    [notify]
  );

  const handleSelectList = (e) => {
    const id = e.target.value;
    setSelectedListId(id);
    setEditorOpen(false);
    loadListDetail(id);
  };

  const openNewEditor = () => {
    setIsNew(true);
    setDraftName("");
    setDraftMembers([]);
    setAddUserId("");
    setEditorOpen(true);
  };

  const openEditEditor = () => {
    if (!currentList) return;
    setIsNew(false);
    setDraftName(currentList.name || "");
    setDraftMembers(
      (currentList.members || []).map((m) => ({
        user_id: m.user_id,
        qty: m.qty,
        slippage: m.slippage,
        swap: m.swap,
      }))
    );
    setAddUserId("");
    setEditorOpen(true);
  };

  const addMember = () => {
    const uid = parseInt(addUserId, 10);
    if (!uid) return;
    if (draftMembers.some((m) => m.user_id === uid)) {
      notify({ type: "error", message: "User already in list." });
      return;
    }
    setDraftMembers((prev) => [...prev, { user_id: uid, qty: 0.01, slippage: 4, swap: 0 }]);
    setAddUserId("");
  };

  const updateMember = (uid, field, value) => {
    setDraftMembers((prev) =>
      prev.map((m) => (m.user_id === uid ? { ...m, [field]: value } : m))
    );
  };

  const removeMember = (uid) => {
    setDraftMembers((prev) => prev.filter((m) => m.user_id !== uid));
  };

  const saveList = async () => {
    const name = (draftName || "").trim();
    if (!name) {
      notify({ type: "error", message: "Enter a list name." });
      return;
    }
    // Validate members
    const members = draftMembers.map((m) => ({
      user_id: m.user_id,
      qty: num(m.qty),
      slippage: num(m.slippage),
      swap: num(m.swap),
    }));
    for (const m of members) {
      if (m.qty <= 0) {
        notify({ type: "error", message: `Qty must be > 0 for ${usersById[m.user_id]?.username || m.user_id}.` });
        return;
      }
    }
    setSaving(true);
    try {
      let saved;
      if (isNew) {
        saved = await createGoldList({ name, members });
      } else {
        saved = await updateGoldList(currentList.id, { name, members });
      }
      notify({ type: "success", message: `List "${name}" saved.` });
      setEditorOpen(false);
      await loadLists();
      setSelectedListId(String(saved.id));
      setCurrentList(saved);
    } catch (err) {
      notify({
        type: "error",
        message: err?.response?.data?.detail || "Failed to save list.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteList = async () => {
    if (!currentList) return;
    if (!window.confirm(`Delete list "${currentList.name}"? This cannot be undone.`)) return;
    try {
      await deleteGoldList(currentList.id);
      notify({ type: "success", message: "List deleted." });
      setSelectedListId("");
      setCurrentList(null);
      setEditorOpen(false);
      await loadLists();
    } catch (err) {
      notify({ type: "error", message: err?.response?.data?.detail || "Failed to delete list." });
    }
  };

  const canRun = currentList && (currentList.members || []).length > 0 && entryPrice && exitPrice;

  const buildBatchPayload = () => ({
    side,
    entry_price: num(entryPrice),
    exit_price: num(exitPrice),
    trade_date: tradeDate || null,
    notes: notes || null,
  });

  const doPreview = async () => {
    if (!canRun) {
      notify({ type: "error", message: "Select a list and enter entry + exit price." });
      return;
    }
    setPreviewing(true);
    setResult(null);
    try {
      const data = await previewGoldBatch(currentList.id, buildBatchPayload());
      setPreview(data);
    } catch (err) {
      notify({ type: "error", message: err?.response?.data?.detail || "Preview failed." });
    } finally {
      setPreviewing(false);
    }
  };

  const doExecute = async () => {
    if (!preview) {
      notify({ type: "error", message: "Run Preview first." });
      return;
    }
    const t = preview.totals || {};
    const warn =
      t.negative_count > 0
        ? `\n\n⚠️ ${t.negative_count} user(s) will go NEGATIVE.`
        : "";
    if (
      !window.confirm(
        `Apply ${side.toUpperCase()} gold entry to ${t.user_count} user(s)?\n` +
          `Entry ${entryPrice} → Exit ${exitPrice}\n` +
          `Total net P&L: ${money(t.total_net)}${warn}`
      )
    ) {
      return;
    }
    setExecuting(true);
    try {
      const payload = { ...buildBatchPayload(), idempotency_key: genKey() };
      const data = await executeGoldBatch(currentList.id, payload);
      setResult(data);
      setPreview(null);
      notify({
        type: "success",
        message: `Gold batch applied to ${data.totals?.users_affected} user(s). Total net ${money(
          data.totals?.total_net
        )}.`,
      });
      if (onAfterExecute) onAfterExecute();
    } catch (err) {
      notify({ type: "error", message: err?.response?.data?.detail || "Execution failed." });
    } finally {
      setExecuting(false);
    }
  };

  const card = styles.card || "bg-white border border-gray-200 rounded-lg";
  const secondary = styles.secondaryCard || "bg-white border border-gray-200 rounded-lg";
  const input = styles.input || "border rounded px-2 py-1 w-full";
  const muted = styles.muted || "text-gray-500";
  const cell = styles.cellClass || "border px-3 py-2";
  const table = styles.tableClass || "min-w-full border-collapse border";
  const headerRow = styles.headerRow || "bg-gray-100";

  // Users available to add (not already in draft)
  const availableUsers = useMemo(() => {
    const inDraft = new Set(draftMembers.map((m) => m.user_id));
    return (users || [])
      .filter((u) => !inDraft.has(u.user_id))
      .sort((a, b) => (a.username || "").localeCompare(b.username || ""));
  }, [users, draftMembers]);

  return (
    <div className={`p-6 rounded-lg shadow-lg ${card}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold">🥇 Manual Gold Entry (XAUUSD)</h2>
          <p className={`text-xs ${muted}`}>
            Batch a completed gold trade across a saved list of users. P&amp;L is applied to each
            user's balance and shown in their history.
          </p>
        </div>
      </div>

      {localMsg && !setStatus && (
        <div
          className={`mb-3 px-3 py-2 rounded text-sm ${
            localMsg.type === "error"
              ? styles.statusError || "bg-red-100 text-red-800"
              : styles.statusSuccess || "bg-green-100 text-green-800"
          }`}
        >
          {localMsg.message}
        </div>
      )}

      {/* ---------- List Manager ---------- */}
      <div className={`p-4 rounded-lg mb-4 ${secondary}`}>
        <p className={`text-xs font-semibold mb-2 ${muted}`}>USER LISTS</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className={`text-[11px] ${muted}`}>Select a list</label>
            <select className={input} value={selectedListId} onChange={handleSelectList}>
              <option value="">— Choose a list —</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.member_count})
                </option>
              ))}
            </select>
          </div>
          <button
            className="px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm"
            onClick={openNewEditor}
          >
            + New List
          </button>
          <button
            className="px-3 py-1 rounded bg-gray-600 hover:bg-gray-500 text-white text-sm disabled:opacity-50"
            onClick={openEditEditor}
            disabled={!currentList}
          >
            Edit
          </button>
          <button
            className="px-3 py-1 rounded bg-red-600 hover:bg-red-500 text-white text-sm disabled:opacity-50"
            onClick={handleDeleteList}
            disabled={!currentList}
          >
            Delete
          </button>
        </div>

        {/* Editor */}
        {editorOpen && (
          <div className={`mt-4 p-3 rounded-lg ${card}`}>
            <div className="mb-2">
              <label className={`text-[11px] ${muted}`}>List name</label>
              <input
                className={input}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="e.g. VIP Gold Batch"
              />
            </div>

            <div className="flex items-end gap-2 mb-2">
              <div className="flex-1">
                <label className={`text-[11px] ${muted}`}>Add user</label>
                <select className={input} value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
                  <option value="">— Select user —</option>
                  {availableUsers.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.username}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
                onClick={addMember}
                disabled={!addUserId}
              >
                Add
              </button>
            </div>

            {draftMembers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className={table}>
                  <thead className={headerRow}>
                    <tr>
                      <th className={cell}>User</th>
                      <th className={cell}>Qty (lots)</th>
                      <th className={cell}>Slippage (pts)</th>
                      <th className={cell}>Swap ($)</th>
                      <th className={cell}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftMembers.map((m) => (
                      <tr key={m.user_id}>
                        <td className={cell}>{usersById[m.user_id]?.username || `#${m.user_id}`}</td>
                        <td className={cell}>
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            className={input}
                            value={m.qty}
                            onChange={(e) => updateMember(m.user_id, "qty", e.target.value)}
                          />
                        </td>
                        <td className={cell}>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            className={input}
                            value={m.slippage}
                            onChange={(e) => updateMember(m.user_id, "slippage", e.target.value)}
                          />
                        </td>
                        <td className={cell}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className={input}
                            value={m.swap}
                            onChange={(e) => updateMember(m.user_id, "swap", e.target.value)}
                          />
                        </td>
                        <td className={cell}>
                          <button
                            className="px-2 py-0.5 rounded bg-red-600 hover:bg-red-500 text-white text-xs"
                            onClick={() => removeMember(m.user_id)}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={`text-xs ${muted}`}>No users added yet.</p>
            )}

            <div className="flex gap-2 mt-3">
              <button
                className="px-4 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-50"
                onClick={saveList}
                disabled={saving}
              >
                {saving ? "Saving..." : isNew ? "Create List" : "Save Changes"}
              </button>
              <button
                className="px-4 py-1 rounded bg-gray-500 hover:bg-gray-400 text-white text-sm"
                onClick={() => setEditorOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Selected list summary */}
        {currentList && !editorOpen && (
          <p className={`text-xs mt-3 ${muted}`}>
            Selected: <span className="font-semibold">{currentList.name}</span> —{" "}
            {(currentList.members || []).length} user(s)
          </p>
        )}
      </div>

      {/* ---------- Execute Panel ---------- */}
      <div className={`p-4 rounded-lg ${secondary}`}>
        <p className={`text-xs font-semibold mb-2 ${muted}`}>EXECUTE BATCH ENTRY</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div>
            <label className={`text-[11px] ${muted}`}>Side</label>
            <select className={input} value={side} onChange={(e) => setSide(e.target.value)}>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>
          <div>
            <label className={`text-[11px] ${muted}`}>Entry Price</label>
            <input
              type="number"
              step="0.01"
              className={input}
              value={entryPrice}
              onChange={(e) => setEntryPrice(e.target.value)}
              placeholder="e.g. 5056"
            />
          </div>
          <div>
            <label className={`text-[11px] ${muted}`}>Exit Price</label>
            <input
              type="number"
              step="0.01"
              className={input}
              value={exitPrice}
              onChange={(e) => setExitPrice(e.target.value)}
              placeholder="e.g. 5475"
            />
          </div>
          <div>
            <label className={`text-[11px] ${muted}`}>Trade Date (optional)</label>
            <input
              type="date"
              className={input}
              value={tradeDate}
              onChange={(e) => setTradeDate(e.target.value)}
            />
          </div>
          <div>
            <label className={`text-[11px] ${muted}`}>Notes (optional)</label>
            <input
              className={input}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="reason / batch note"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-3">
          <button
            className="px-4 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white text-sm disabled:opacity-50"
            onClick={doPreview}
            disabled={!canRun || previewing}
          >
            {previewing ? "Previewing..." : "Preview"}
          </button>
          <button
            className="px-4 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm disabled:opacity-50"
            onClick={doExecute}
            disabled={!preview || executing}
          >
            {executing ? "Submitting..." : "Submit (Apply P&L)"}
          </button>
        </div>

        {/* Preview table */}
        {preview && (
          <div className="mt-4 overflow-x-auto">
            <p className={`text-xs mb-1 ${muted}`}>
              Preview — {preview.totals?.user_count} user(s), total net{" "}
              <span className="font-semibold">{money(preview.totals?.total_net)}</span>
              {preview.totals?.negative_count > 0 && (
                <span className="text-red-500 font-semibold">
                  {" "}
                  · {preview.totals.negative_count} would go negative
                </span>
              )}
            </p>
            <table className={table}>
              <thead className={headerRow}>
                <tr>
                  <th className={cell}>User</th>
                  <th className={cell}>Qty (lots)</th>
                  <th className={cell}>Slip</th>
                  <th className={cell}>Swap</th>
                  <th className={cell}>Net P&amp;L</th>
                  <th className={cell}>Balance →</th>
                </tr>
              </thead>
              <tbody>
                {(preview.rows || []).map((r) => (
                  <tr key={r.user_id} className={r.will_go_negative ? "bg-red-500/10" : ""}>
                    <td className={cell}>{r.username}</td>
                    <td className={cell}>{r.qty}</td>
                    <td className={cell}>{r.slippage}</td>
                    <td className={cell}>{money(r.swap)}</td>
                    <td className={`${cell} ${r.net_pnl < 0 ? "text-red-500" : "text-emerald-500"}`}>
                      {money(r.net_pnl)}
                    </td>
                    <td className={cell}>
                      {money(r.current_balance)} → {money(r.projected_balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Result summary */}
        {result && (
          <div className="mt-4 overflow-x-auto">
            <p className={`text-xs mb-1 ${muted}`}>
              ✅ Applied to {result.totals?.users_affected} user(s) — total net{" "}
              <span className="font-semibold">{money(result.totals?.total_net)}</span>{" "}
              <span className={muted}>(batch {result.batch_id})</span>
            </p>
            <table className={table}>
              <thead className={headerRow}>
                <tr>
                  <th className={cell}>User</th>
                  <th className={cell}>Position ID</th>
                  <th className={cell}>Net P&amp;L</th>
                  <th className={cell}>New Balance</th>
                </tr>
              </thead>
              <tbody>
                {(result.per_user || []).map((r) => (
                  <tr key={r.user_id}>
                    <td className={cell}>{r.username}</td>
                    <td className={cell}>{r.position_id}</td>
                    <td className={`${cell} ${r.net_pnl < 0 ? "text-red-500" : "text-emerald-500"}`}>
                      {money(r.net_pnl)}
                    </td>
                    <td className={cell}>{money(r.new_balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
